import axios, { type InternalAxiosRequestConfig } from 'axios'

import { config } from '../config'
import { attachMicroserviceAuthRetry } from '../sso/interceptors/microservice-auth-retry.interceptor'
import { resolveSystemUsersAccessToken } from '../sso/services/system-users-access.service'
import { isSuiteSsoRedirectMode } from '../sso/sso-flags'
import { logWarn } from '../sso/utils/sso-logger'

function getStoredPayRefreshToken(): string {
  const raw = globalThis.sessionStorage.getItem('token') || globalThis.localStorage.getItem('token')
  return raw?.replaceAll('"', '') ?? ''
}

/**
 * System users API client with automatic auth.
 * In SSO mode always uses identity JWT so existing requestAccessToken call sites keep working.
 */
export const SystemUsersHttp = axios.create({
  timeout: 9000000,
  baseURL: config.api.systemUsers.baseUrl,
  withCredentials: false
})

SystemUsersHttp.interceptors.request.use(async (requestConfig: InternalAxiosRequestConfig) => {
  if (isSuiteSsoRedirectMode()) {
    const payAccessToken = await resolveSystemUsersAccessToken(getStoredPayRefreshToken())
    requestConfig.headers.Authorization = `Bearer ${payAccessToken}`
    return requestConfig
  }

  const hasExplicitAuth =
    typeof requestConfig.headers?.Authorization === 'string' &&
    requestConfig.headers.Authorization.length > 0

  if (hasExplicitAuth) {
    return requestConfig
  }

  try {
    const accessToken = await resolveSystemUsersAccessToken(getStoredPayRefreshToken())
    requestConfig.headers.Authorization = `Bearer ${accessToken}`
  } catch (error) {
    logWarn('SystemUsersHttp — could not resolve system-users access token', { error })
    throw error
  }

  return requestConfig
})

attachMicroserviceAuthRetry(SystemUsersHttp)
